import { clientFetch, url } from "@/components/shared/fetch-client/fetch_client";
import { bytesToStorageString, round_to_decimal_place } from "@/components/shared/utils";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import BackupOutlinedIcon from "@mui/icons-material/BackupOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";

/* -------------------------------------------------------------------------- */
/*                              CARD PROGRESS BAR                             */
/* -------------------------------------------------------------------------- */
export default function CardProgressBar({ trans_key, product }) {
	// Hooks
	const { t } = useTranslation();
	const [productUsage, setProductUsage] = useState(null);
	const [productUsageError, setProductUsageError] = useState(false);
	const [productUsageLoading, setProductUsageLoading] = useState(true);

	if (product.datastore_config) {
		product.config = product.datastore_config;
	}

	// const use_tb_bytes_format = product.is_free_version ? false : true
	const { alert_threshold, alert_threshold_bytes, alert_email_address, config } = product;
	// Set value to false if undefined
	if (!product.is_free_version) {
		product.is_free_version = false;
	}

	// Logic
	const formattedAlertThresholdString = bytesToStorageString(product.alert_threshold_bytes);

	// Trans key
	const transKeyProgressBar = `${trans_key}.manage.overview.card.progress`;

	// Handlers
	const determTooltipMessage = () => {
		switch (config) {
			case "auto_expand":
				return (
					<Trans
						i18nKey={`${transKeyProgressBar}.auto_expand.notification`}
						values={{
							value: formattedAlertThresholdString,
						}}
					/>
				);
			case "limited_size":
				return (
					<Trans
						i18nKey={`${transKeyProgressBar}.limited_size.notification`}
						values={{
							value: formattedAlertThresholdString,
						}}
					/>
				);
		}
	};

	const determBytesInUseValue = () => {
		// Return placeholder when unknown
		if (!productUsage?.bytes_in_use) {
			return "- GB";
		}

		const usageStorageString = bytesToStorageString(productUsage.bytes_in_use);
		return usageStorageString;
	};
	const determBytesQuotaValue = () => {
		// Return placeholder when unknown
		if (!productUsage?.bytes_quota) {
			return "- GB";
		}

		const quotaStorageString = bytesToStorageString(productUsage.bytes_quota);
		const percentage = (productUsage.bytes_in_use / productUsage.bytes_quota) * 100;
		const percentageRounded = round_to_decimal_place(percentage, 2);

		return `${quotaStorageString} - (${percentageRounded}%)`;
	};

	// Listeners
	useEffect(() => {
		const fetchProductUsage = async () => {
			// Set loading false
			setProductUsageLoading(false);

			// Fetch data
			const { ok, body } = await clientFetch(url`/api/${trans_key}/${product.id}/usage`).get();

			// Try to fetch again after 2 seconds if failed request
			if (!ok) {
				setTimeout(() => {
					fetchProductUsage();
				}, 2000);
			}

			// Set product usage
			setProductUsage(body);
			setProductUsageError(body.bytes_in_use < 0 || body.bytes_quota < 0);
		};

		if (!productUsage && productUsageLoading) {
			fetchProductUsage();
		}
	}, [product.id, trans_key, productUsage, productUsageLoading]);

	return (
		<Box sx={{ px: 2 }}>
			<Paper variant="outlined" sx={{ pb: 4, position: "relative", pt: 1.5, px: 2 }}>
				<Grid container display="flex" alignItems="center">
					<Grid item xs={12}>
						<Typography fontWeight={500}>{t(`${transKeyProgressBar}.title`)}</Typography>
					</Grid>

					{productUsageError ? (
						<Grid item xs={12}>
							<Alert variant="outlined" severity="warning" sx={{ mb: -1, mt: 1 }}>
								{t(`${transKeyProgressBar}.no_usage_alert`)}
							</Alert>
						</Grid>
					) : (
						<>
							{/* Usage section  */}

							{productUsage ? (
								<Grid item xs={12} display="flex" alignItems="center">
									<BackupOutlinedIcon sx={{ height: 16, mr: 0.5, width: 16 }} />
									{config === "limited_size" ? (
										<Typography fontSize={14}>
											<Trans
												i18nKey={`${transKeyProgressBar}.limited_size.data_usage`}
												values={{
													current: determBytesInUseValue(),
													total: determBytesQuotaValue(),
												}}
											/>
										</Typography>
									) : (
										<Typography fontSize={14} display="flex" alignItems="center">
											<Box component="span">{determBytesInUseValue()}</Box>
											<Box component="span" px={0.5}>
												/
											</Box>
											<AllInclusiveIcon sx={{ height: 16, width: 16 }} />
										</Typography>
									)}
								</Grid>
							) : (
								<Grid item xs={12} display="flex" alignItems="center">
									<Skeleton sx={{ mt: 0.0125, width: "80%" }} />
								</Grid>
							)}

							{/* Threshold info  */}
							{productUsage ? (
								<Grid item xs={12} sx={{ mt: 0.5 }}>
									{alert_threshold ? (
										<Tooltip title={determTooltipMessage()} followCursor>
											<Box display="flex" alignItems="center" fontSize={14}>
												<NotificationsActiveIcon sx={{ height: 16, mr: 0.25, width: 16 }} />
												<Box component="span">{formattedAlertThresholdString}</Box>
												<ArrowForwardIcon sx={{ height: 16, mx: 0.5, width: 16 }} />
												<MailOutlineIcon sx={{ height: 16, mr: 0.25, width: 16 }} />
												<Box component="span">{alert_email_address}</Box>
											</Box>
										</Tooltip>
									) : (
										<Box height={24} />
									)}
								</Grid>
							) : (
								<Grid item xs={12} display="flex" alignItems="center">
									<Skeleton sx={{ mb: 0.25, mt: 0.25, width: "80%" }} />
								</Grid>
							)}

							{/* Progress Bar  */}
							{productUsage ? (
								<ProgressBar
									config={config}
									alert_threshold={alert_threshold}
									alert_threshold_bytes={alert_threshold_bytes}
									current_usage={determBytesInUseValue()}
									product_usage={productUsage}
									product_usage_error={productUsageError}
									determ_tooltip_message={determTooltipMessage}
									trans_key_progress_bar={transKeyProgressBar}
								/>
							) : (
								<Grid item xs={12} mt={1}>
									<Skeleton sx={{ mt: -1.1 }} />
								</Grid>
							)}
						</>
					)}
				</Grid>
			</Paper>
		</Box>
	);
}

function ProgressBar({
	config,
	alert_threshold,
	alert_threshold_bytes,
	product_usage,
	product_usage_error,
	determ_tooltip_message,
	current_usage,
	trans_key_progress_bar,
}) {
	// Variables
	const options = {
		borderRadius: 8,
		height: 14,
	};

	// Handlers
	const percentage = round_to_decimal_place(
		(product_usage.bytes_in_use / product_usage.bytes_quota) * 100,
		2,
	);

	return (
		<Grid item xs={12} mt={1}>
			<Box
				sx={{
					position: "relative",
				}}
			>
				{/* Progress bar  */}
				<LinearProgress
					sx={{ borderRadius: 8, height: options.height }}
					color="info"
					variant="determinate"
					value={percentage}
				/>

				{/* Position indicator */}
				<Tooltip
					title={current_usage}
					open={true}
					arrow
					hidden={product_usage_error}
					PopperProps={{
						sx: {
							zIndex: 90, // Lower z-index value to prevent the tooltip from appearing on top of the TitleBar and Header
						},
					}}
				>
					<Box
						sx={{
							height: 0,
							left: `${percentage}%`,
							lineHeight: 0,
							position: "absolute",
							top: options.height - 8,
							transition: "ease",
							width: 0,
						}}
					/>
				</Tooltip>

				{/* Notification indicator  */}
				<NotificationIndicator
					config={config}
					alert_threshold={alert_threshold}
					alert_threshold_bytes={alert_threshold_bytes}
					product_usage={product_usage}
					options={options}
					determ_tooltip_message={determ_tooltip_message}
				/>

				{/* Auto-resize indicator  */}
				<AutoExpandIndicator
					config={config}
					options={options}
					trans_key_progress_bar={trans_key_progress_bar}
				/>
			</Box>
		</Grid>
	);
}

function NotificationIndicator({
	config,
	alert_threshold,
	alert_threshold_bytes,
	product_usage,
	options,
	determ_tooltip_message,
}) {
	// Hooks
	const iconSize = 20;

	// Don't show when alert threshold is false
	if (!alert_threshold) {
		return null;
	}

	const bLimit = product_usage.bytes_quota;
	const bAlert = alert_threshold_bytes;

	// If alert higher than max disk size (Grow as you go)
	if (bAlert > bLimit) {
		return null;
	}

	const alertPercentage =
		config === "limited_size" ? ((bLimit - bAlert) / bLimit) * 100 : (bAlert / bLimit) * 100;

	return (
		<Box position="absolute" sx={{ left: `${alertPercentage}%`, top: 0 }}>
			<Box
				sx={{
					backgroundColor: "grey.main",
					height: options.height,
					width: "2px",
				}}
			/>

			<Box
				mt={0.5}
				display="flex"
				ml="calc(-100% + 1px)"
				flexDirection="column"
				justifyContent="center"
				alignItems="center"
			>
				<Tooltip title={determ_tooltip_message()}>
					<NotificationsActiveIcon sx={{ cursor: "pointer", height: iconSize, width: iconSize }} />
				</Tooltip>
			</Box>
		</Box>
	);
}

function AutoExpandIndicator({ config, options, trans_key_progress_bar }) {
	// Hooks
	const { t } = useTranslation();
	const iconSize = 20;

	if (config !== "auto_expand") {
		return null;
	}

	return (
		<Box position="absolute" sx={{ left: "95%", top: 0 }}>
			<Box
				sx={{
					backgroundColor: "#4d4d4d",
					height: options.height,
					width: "2px",
				}}
			/>

			<Box
				mt={0.5}
				display="flex"
				ml="calc(-100% + 1px)"
				flexDirection="column"
				justifyContent="center"
				alignItems="center"
			>
				<Tooltip title={t(`${trans_key_progress_bar}.auto_expand.chart_icon`)}>
					<AutoGraphIcon sx={{ cursor: "pointer", height: iconSize, width: iconSize }} />
				</Tooltip>
			</Box>
		</Box>
	);
}
